import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import {
    selectCurrentProject,
    selectImageData,
    selectIsLoadingScript,
    selectIsLoadingSharing,
    selectScriptData,
} from '../../store/selectors/projectSelectors';
import WordsCount from './widgets/words-count/WordsCount';
import ToolsContainer from './widgets/tools-container/ToolsContainer';
import Loader from '../../shared/ui/loader/Loader';
import { formatScriptData, htmlToPlainText } from '../../shared/helpers/transformData';
import s from './TextEditorPage.module.css';
import 'quill/dist/quill.snow.css';
import './customQuil.css';
import ReactQuill from 'react-quill';
import { useFetchProject } from '../../shared/hooks/useFetchProject';
import { usePostProject } from '../../shared/hooks/usePostProject';
import { StyledReactQuill } from './quill/StyledReactQuill';
import { modules } from './quill/quillConfig';
// import { SharedModal } from './widgets/shared-modal/SharedModal';
import { useDebounce } from '../../shared/hooks/useDebounce';
import { useAppDispatch } from '../../store/store';
import GeneratedImage from './widgets/generated-image/GeneratedImage';
import { selectAddedHashtag } from '../../store/selectors/pageSelectors';
import { setAddedHashtag } from '../../store/slices/pageSlice';
import ChoosePromptPanel from './widgets/choose-prompt-panel/ChoosePromptPanel';
import NotesSection from './widgets/notes-section/NotesSection';
import PromptTextEditToolbar from './widgets/prompt-tool-bar/PromptTextEditToolbar';
import { updateProject, updateProjectText } from '../../shared/api/textProjectApi';
import { setCurrentProject, setImageData, setScriptData } from '../../store/slices/projectSlice';
import { setProjectNotes } from '../../store/slices/notesSlice';
import ChangeTitle from './widgets/change-title/ChangeTitle';
import ShareButton from './widgets/share-button/ShareButton';
import LogoutButton from '../../shared/ui/logout-button/LogoutButton';

const TextEditorPage: React.FC = () => {
    const quillRef = useRef<ReactQuill | null>(null);
    const isFirstDebounce = useRef(true);

    const currentProject = useSelector(selectCurrentProject);
    const scriptData = useSelector(selectScriptData);
    const isLoadingScript = useSelector(selectIsLoadingScript);
    const isLoadingShare = useSelector(selectIsLoadingSharing);
    const addedHashtag = useSelector(selectAddedHashtag);
    const imageData = useSelector(selectImageData);

    const [value, setValue] = useState<string>('');
    const [imageUploadContainer, setImageUploadContainer] = useState<HTMLElement | null>(null);
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    const [isImageFocused, setIsImageFocused] = useState(false);
    const [containerPosition, setContainerPosition] = useState<{
        top: number;
        right: number;
    } | null>(null);

    const debouncedValue = useDebounce(value, 2000);
    const dispatch = useAppDispatch();

    const imageRef = useRef<HTMLDivElement | null>(null);
    const toolsRef = useRef<HTMLDivElement | null>(null);

    // const { isPosted, setIsPosted } = usePostProject();

    useFetchProject();

    useEffect(() => {
        if (debouncedValue.trim() === '' || !currentProject?.id) {
            return;
        }
        if (isFirstDebounce.current) {
            isFirstDebounce.current = false;
            return;
        }

        // const plainText = htmlToPlainText(debouncedValue);
        dispatch(updateProjectText({ projectId: currentProject.id, body: debouncedValue }));
    }, [debouncedValue, currentProject?.id, dispatch]);

    useEffect(() => {
        if (scriptData) {
            (async () => {
                const formattedText = await formatScriptData(scriptData);
                setValue(formattedText);
            })();
        }
    }, [scriptData]);

    const handleSetValue = (value: string) => {
        setValue(value);
    };
    const handleUpdateTitle = async (value: string) => {
        await dispatch(updateProject({ ...currentProject, title: value }));
    };

    useEffect(() => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            const editorElement = editor.root;
            const parentContainer = editorElement?.parentElement;

            if (parentContainer && currentProject) {
                const imageContainer = document.createElement('div');
                imageContainer.classList.add(s.image_container);

                if (
                    currentProject.content_type &&
                    currentProject.content_type.toLowerCase() === 'instagram'
                ) {
                    parentContainer.insertBefore(imageContainer, editorElement);
                } else {
                    parentContainer.appendChild(imageContainer);
                }
                setImageUploadContainer(imageContainer);
            }
        }
    }, [currentProject?.content_type]);

    // useEffect(() => {
    //     if (!addedHashtag || !quillRef.current) return;

    //     const editor = quillRef.current.getEditor();
    //     const range = editor.getSelection();

    //     if (range) {
    //         editor.insertText(range.index, ' ' + addedHashtag, 'user');
    //         editor.setSelection({ index: range.index + addedHashtag.length + 1, length: 0 });
    //     } else {
    //         const length = editor.getLength();
    //         editor.insertText(length, ' ' + addedHashtag, 'user');
    //         editor.setSelection({ index: length + addedHashtag.length + 1, length: 0 });
    //     }

    //     dispatch(setAddedHashtag(''));
    // }, [addedHashtag]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            const target = e.target as Node;
            const editorEl = quillRef.current?.getEditor().root;
            const toolbarEl = document.querySelector('.ql-toolbar');
            const imageEl = imageRef.current;
            const toolsEl = toolsRef.current;

            const inside = (el?: Element | null) => el?.contains(target);

            if (inside(editorEl)) {
                setIsEditorFocused(true);
                setIsImageFocused(false);
                editorEl!.classList.add('focused');
                const { top, right } = editorEl!.getBoundingClientRect();
                setContainerPosition({ top, right });
            } else if (inside(imageEl)) {
                setIsEditorFocused(false);
                setIsImageFocused(true);
                editorEl?.classList.remove('focused');
                const { top, right } = imageEl!.getBoundingClientRect();
                setContainerPosition({ top, right: right + 20 });
            } else if (inside(toolsEl) || inside(toolbarEl)) {
            } else {
                setIsEditorFocused(false);
                setIsImageFocused(false);
                editorEl?.classList.remove('focused');
                setContainerPosition(null);
            }
        };

        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    useEffect(() => {
        return () => {
            dispatch(setCurrentProject(null));
            dispatch(setImageData(null));
            dispatch(setScriptData(null));
            dispatch(setProjectNotes([]));
        };
    }, []);

    return currentProject != null ? (
        <div className={s.content}>
            <div className={s.left_column}>
                <ChangeTitle initialTitle={currentProject?.title} updateText={handleUpdateTitle} />
                <ChoosePromptPanel type={'text'} />
                <NotesSection />
            </div>
            <div className={s.center_column}>
                <StyledReactQuill
                    ref={quillRef}
                    value={value}
                    onChange={handleSetValue}
                    modules={modules}
                    className={`${s.quill} ${isEditorFocused ? s.focused : ''}`}
                />
                <ToolsContainer
                    ref={toolsRef}
                    value={value}
                    isEditorFocused={isEditorFocused}
                    isImageFocused={isImageFocused}
                    containerPosition={containerPosition}
                    quillRef={quillRef}
                    onCloseContext={() => {
                        setIsEditorFocused(false);
                        setIsImageFocused(false);
                    }}
                    pageType={'text'}
                    isShow={imageData !== null}
                />
            </div>

            <div className={s.right_column}>
                <WordsCount value={value} quillRef={quillRef} />
                <div className={s.action_buttons}>
                    <ShareButton />
                    <LogoutButton />
                </div>

                <PromptTextEditToolbar quillRef={quillRef} />
            </div>

            {imageUploadContainer &&
                createPortal(
                    <GeneratedImage
                        imageUrl={imageData?.url ?? null}
                        currentProject={currentProject}
                        isFocused={isImageFocused}
                        ref={imageRef}
                        imageId={imageData?.id ?? null}
                    />,
                    imageUploadContainer,
                )}

            {/* <SharedModal isOpen={isPosted} onClose={() => setIsPosted(false)} /> */}

            {(isLoadingScript || isLoadingShare) && <Loader />}
        </div>
    ) : (
        <Loader />
    );
};

export default TextEditorPage;
